/**
 * Platform Console (Phase 7) — super-admin tenant management.
 *
 * Wraps the app on the platform host (admin.konklave.app). It:
 *  - gates the console to super-admins,
 *  - shows a tenant switcher that sets the active org (persisted) and reloads,
 *    so every subsequent API call carries the chosen x-organization-id.
 * On any non-platform host it's a transparent passthrough.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useAdminAccess } from '../../hooks/useAdminAccess';
import { apiRequest } from '../../config/api';
import {
  isPlatformAdminHost,
  getActiveOrgId,
  setActiveOrgId,
  PLATFORM_ORG_SENTINEL,
} from '../../config/tenant';
import AccessDenied from '../shared/AccessDenied';

const BANNER_H = 52;

interface OrgSummary {
  id: string;
  name?: string;
  appName?: string;
  domain?: string;
  billingMode?: string | null;
}

const Banner: React.FC = () => {
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const { user, signOut } = useAuth();
  const active = getActiveOrgId();

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      window.location.assign('/');
    }
  };

  const accountLabel =
    (user as any)?.email || (user as any)?.attributes?.email || (user as any)?.username || '';

  useEffect(() => {
    let cancelled = false;
    apiRequest<OrgSummary[]>('/api/organizations')
      .then(list => {
        if (!cancelled) setOrgs(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        /* leave empty; current org still shown */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSwitch = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    if (next && next !== active) {
      setActiveOrgId(next);
      window.location.reload();
    }
  };

  const onSentinel = active === PLATFORM_ORG_SENTINEL;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: BANNER_H,
        zIndex: 2000,
        background: '#5E0D19',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '0 18px',
        boxShadow: '0 2px 8px rgba(0,0,0,.25)',
      }}
    >
      <span style={{ fontWeight: 700, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
        KONKLAVE <span style={{ color: '#D4A11E' }}>· Platform Admin</span>
      </span>
      <label
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: '0.85rem',
        }}
      >
        <span style={{ color: '#B9C2D6' }}>Managing tenant:</span>
        <select
          value={active}
          onChange={onSwitch}
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid #2A3A5C',
            background: '#fff',
            color: '#14233F',
            fontWeight: 600,
            minWidth: 200,
          }}
        >
          {onSentinel && <option value={PLATFORM_ORG_SENTINEL}>— Select a tenant —</option>}
          {orgs.map(o => (
            <option key={o.id} value={o.id}>
              {o.name || o.appName || o.id} ({o.id})
            </option>
          ))}
        </select>
      </label>
      {/* Tenant-level settings (billing, features, etc.) live in the Tenants admin. */}
      <a
        href="/admin/tenants"
        style={{
          color: '#fff',
          fontSize: '0.82rem',
          fontWeight: 600,
          textDecoration: 'none',
          border: '1px solid #2A3A5C',
          borderRadius: 6,
          padding: '6px 12px',
          whiteSpace: 'nowrap',
        }}
      >
        Tenants
      </a>
      {accountLabel && (
        <span
          style={{ color: '#B9C2D6', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
          title={accountLabel}
        >
          {accountLabel}
        </span>
      )}
      <button
        type="button"
        onClick={handleSignOut}
        style={{
          padding: '6px 12px',
          borderRadius: 6,
          border: '1px solid #2A3A5C',
          background: 'transparent',
          color: '#fff',
          fontWeight: 600,
          fontSize: '0.8rem',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        Sign out
      </button>
    </div>
  );
};

const PlatformConsole: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { isSuperAdmin } = useAdminAccess();

  if (!isPlatformAdminHost()) return <>{children}</>;

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          color: '#5B6477',
        }}
      >
        Loading platform console…
      </div>
    );
  }

  // Signed in but not a super-admin → no access.
  if (user && !isSuperAdmin()) {
    return <AccessDenied message="The platform console is restricted to super-admins." />;
  }

  // Super-admin: show the switcher banner above the app. (Unauthenticated users
  // fall through to the normal sign-in flow rendered by the routes below.)
  const showBanner = Boolean(user && isSuperAdmin());
  return (
    <>
      {showBanner && <Banner />}
      <div style={{ paddingTop: showBanner ? BANNER_H : 0 }}>{children}</div>
    </>
  );
};

export default PlatformConsole;
